/* ================================================================ */
// LAYOUT CONTAINER | Layout.jsx
/* ================================================================ */
/* ========================================== */
// Imports
/* ========================================== */
import React from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import {connect} from 'react-redux';
import {getLoadingStatusV2, getGlobeTitle, getGlobeConfig, getGlobeTheme, getError} from '../../selectors/Data';
import { BrowserRouter, Route, Switch, Redirect} from 'react-router-dom';
//import { createBrowserHistory } from 'history';
// Util
import {isNullOrUndefined} from 'util';
// Auth0
import { useAuth0 } from '../../auth0/auth0';
// MaterialUI
import Fab from '@material-ui/core/Fab';
import ThemeProvider from '@material-ui/styles/ThemeProvider';
import DarkModeIcon from '@material-ui/icons/Brightness4';
// Constants
import {LayoutTypes, AlignmentTypes, NavigationTypes, MenuTypes} from '@myglobe/core/dist/types';
// Validator
import LayoutConfig from './LayoutConfig';
import useDarkMode from '../../utils/useThemeMode';
// Local Components
import Loading from '../../containers/Loading';
import Spritesheet from '../../containers/Sprite/Spritesheet';
import AdminLayout from './AdminLayout';
import MainLayout from './MainLayout';

// Assets
import {getTheme} from '@myglobe/core/dist/styles/theme';

//const history = createBrowserHistory();
// TODO: fetch base theme from state via mode parameter
//const location = history.location;

/* ========================================== */
/**
 * Layout
 * ------
 * App layout
 *
 * @param {object} props component props
 * @param {string} props.title globe title
 * @param {object} props.config layout config
 * @param {object} props.theme globe theme
 * @param {boolean} props.isLoading loading status
 */
/* ========================================== */
const Layout = ({title, config, theme, isLoading, error}) => {
    const [darkMode, setDarkMode] = useDarkMode();
    const MyGlobeTheme = getTheme(darkMode ? 'dark' : 'light');
    //const useForceUpdate = () => useState()[1];
    //const MainLayout = useLayout(type, {});
    //const forceUpdate = useForceUpdate();
    /* ======================== */
    // COMPONENT MOUNT
    /* ======================== */
    /*useEffect(()=>{
        /* ========================================== */
    /**
         * Update Component Event Handler
         * ------------------------------
         * Forces updating on layout component
         * (used for print media changes)
         *
         * @param {Event} event
         */
    /*function updateComponent(event) {
            ReactDOM.flushSync(() => {
                event.matches;
                forceUpdate();
            });
        }
        window.matchMedia('print').addListener(updateComponent);
    /* ======================== */
    // UNMOUNT
    /* ======================== */
    /*  return () => {
            window.matchMedia('print').removeListener(updateComponent);
        };
    }, [forceUpdate]);
    */
    /* ======================== */
    // HANDLERS
    /* ======================== */
    function toggleDarkMode() {
        setDarkMode(!darkMode);
    }
    /* ========================================== */
    /**
     * Toggle Full Screen
     * ------------------
     * Changes window to full screen mode
     */
    function toggleFullScreen() {
        var doc = window.document;
        var docEl = doc.documentElement;

        var requestFullScreen = docEl.requestFullscreen || docEl.mozRequestFullScreen || docEl.webkitRequestFullScreen || docEl.msRequestFullscreen;
        var cancelFullScreen = doc.exitFullscreen || doc.mozCancelFullScreen || doc.webkitExitFullscreen || doc.msExitFullscreen;

        if(!doc.fullscreenElement && !doc.mozFullScreenElement && !doc.webkitFullscreenElement && !doc.msFullscreenElement) {
            requestFullScreen.call(docEl);
            this.resize();
        }
        else {
            cancelFullScreen.call(doc);
        }
    }
    /* ======================== */
    // RENDER
    /* ======================== */
    console.debug(`${Layout.displayName} | `, 'render - ', 'theme:', MyGlobeTheme);
    const Admin = (routeProps) => <AdminLayout />;
    const Main = (routeProps) => !isLoading && (
        <ThemeProvider theme={theme}>
            <MainLayout isError={!isNullOrUndefined(error)} error={error} isLoading={isLoading} config={config} />
        </ThemeProvider>
    );/*, (prevProps, nextProps) => {
        return prevProps.isLoading === nextProps.isLoading && config
    });*/
    const SpritesheetC = (routeProps) => (
        <main styles={{
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            height: '100vh',
            display: 'flex',
            transition: 'all 500ms cubic-bezier(0.215, 0.610, 0.355, 1.000)'
        }}
        >
            <Spritesheet />
        </main>
    );
    return (
        <BrowserRouter /*basename="/gea"*/>
            <ThemeProvider theme={MyGlobeTheme}>
                <Helmet>
                    <title>{title}</title>
                </Helmet>
                <Loading key="loading-overlay" />
                <Switch>
                    {/*<Route path="/admin" component={Admin} />
                    <Route path="/sprite" component={SpritesheetC} />*/}
                    <Route path="/:category/:page" component={Main} />
                    <Route path="/:category" component={Main} />
                    <Redirect exact from="/" to="/introduction" />
                    <Route path="/" component={Main} />
                </Switch>
            </ThemeProvider>
        </BrowserRouter>
    );
};
/* ========================================== */
// Props
/* ========================================== */
Layout.propTypes = {
    screen: PropTypes.shape({
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired
    }),
    title: PropTypes.string.isRequired,
    config: PropTypes.shape({
        logo: PropTypes.string,
        mobile: LayoutConfig,
        desktop: LayoutConfig,
        navigation: PropTypes.shape({
            type: PropTypes.oneOf(Object.values(NavigationTypes)),
            align: PropTypes.oneOf(Object.values(AlignmentTypes)),
            props: PropTypes.object,
            menu: PropTypes.shape({
                type: PropTypes.oneOf(Object.values(MenuTypes)),
                items: PropTypes.arrayOf(PropTypes.shape({
                    title: PropTypes.string.isRequired,
                    path: PropTypes.string.isRequired,
                    position: PropTypes.number,
                    items: PropTypes.arrayOf(PropTypes.shape({
                        title: PropTypes.string.isRequired,
                        path: PropTypes.string.isRequired,
                        position: PropTypes.number,
                    }))
                }))
            })
        })
    }),
    isLoading: PropTypes.bool.isRequired,
    theme: PropTypes.object
};
Layout.types = LayoutTypes;
Layout.displayName = 'Layout';
Layout.whyDidYouRender = true;
/* ========================================== */
// Redux
/* ========================================== */
const mapStateToProps = store => {
    const getLoading = getLoadingStatusV2('globe');
    return {
        title: getGlobeTitle(store),
        config: getGlobeConfig(store),
        theme: getGlobeTheme(store),
        isLoading: getLoading(store),
        error: getError(store)
    };
};
/* ========================================== */
// Export
/* ========================================== */
export default connect(mapStateToProps)(Layout);//(React.memo(Layout, areEqual));
function areEqual(prevProps, nextProps) {
    const isEqual = prevProps.isLoading === nextProps.isLoading;
    console.debug(`${Layout.displayName} | `, 'Prop Check -', 'prevProps:', prevProps, 'nextProps:', nextProps, 'isEqual:', isEqual);
    return isEqual;
}